
import { computed, useContext, onMounted } from '@nuxtjs/composition-api';
import useFlow from '@/composable/checkout/config/useFlow';
import { FLOW_STEP_STATUS } from '@/constants/FlowStepStatus';
import { CHECKOUT_FLOW, CUSTOMER_TYPE, FLOW_STATUS } from '@/constants/CheckoutFlow';
import { STEP_TYPE } from '@/constants/StepType';
import useTracking from '~/composable/tracking/useTracking';
import useCheckout from '@/composable/checkout/useCheckout';
import { useFlowStore } from '@/stores/useFlowStore';

export default {
  setup () {
    const { app } = useContext()
    const { state, checkoutType } = useCheckout(app)

    const { track } = useTracking()
    const flowStore = useFlowStore()

    const {
      isComplete,
      panelModel,
      valueComparator,
      currentStep,
      steps,
      editStep,
      nextStep,
      waitTime,
      performStepFailure,
      next,
      initializeFlow,
      panels,
      preValidate,
      setFlowStepStatus,
      flowStatus
    } = useFlow(app, state)
    initializeFlow(app)

    // CVR + device-without-subscription: skip Kundeoplysninger step in accordion
    const isCvrNoSub = computed(() => {
      return checkoutType?.value === CHECKOUT_FLOW.WITH_OUT_SUBSCRIPTION &&
        state?.customerInfo?.menu?.value === CUSTOMER_TYPE.BUSINESS
    })

    const displayPanels = computed(() => {
      if (!isCvrNoSub.value) {
        return panels.map((p, i) => ({ panel: p, stepIndex: i }))
      }
      return panels
        .map((p, i) => ({ panel: p, stepIndex: i }))
        .filter(e => e.panel.stepType !== STEP_TYPE.CUSTOMER_INFO_CONFIRMATION)
    })

    // Map real step index to display panel index for CVR no-sub
    const stepToDisplay = (step) => (isCvrNoSub.value ? { 0: 0, 1: 0, 2: 1, 3: 2 }[step] ?? 0 : step)

    const activePanelModel = computed({
      get: () => stepToDisplay(currentStep.value),
      set: () => {}
    })
    const activeValueComparator = (_a, b) => b === activePanelModel.value

    // For CVR no-sub: finalize Kundeforhold + Kundeoplysninger in one step, jump to Opsummering
    const goCvrToOpsummering = async () => {
      const { flowHandler, panels: p } = state
      await flowHandler.finalizeStep(p[0].stepType, state, app)
      await flowHandler.finalizeStep(p[1].stepType, state, app)
      flowStore.setStepStatus(0, FLOW_STEP_STATUS.COMPLETE)
      flowStore.setStepStatus(1, FLOW_STEP_STATUS.COMPLETE)
      flowStore.setCurrentStep(2)
      flowStore.updatePanelModel()
      $nuxt.$emit('flowStepNext', 1)
    }

    onMounted(() => {
      $nuxt.$on('formSubmitted', async () => {
        const step = currentStep.value
        if (isCvrNoSub.value && step === 0) {
          await goCvrToOpsummering()
          return
        }
        await next(step)
      })
      $nuxt.$on('formValid', ($event) => {
        if ($event.formValidity) {
          setFlowStepStatus(currentStep.value, FLOW_STEP_STATUS.FORM_VALID)
        } else {
          setFlowStepStatus(currentStep.value, FLOW_STEP_STATUS.INCOMPLETE)
        }
      })
      $nuxt.$on('preValidate', () => {
        preValidate()
      })
      $nuxt.$on('flowStepFailure', () => {
        flowStatus.value = FLOW_STATUS.ERROR
        performStepFailure(currentStep.value)
      })
      $nuxt.$on('flowStepEdit', () => {
        const stepIndex = currentStep.value
        const name = state?.panels?.[stepIndex]?.heading ?? ''
        track('checkoutStepLoad', { currentStep, name })
      })
      $nuxt.$on('flowStepNext', (i) => {
        // For CVR no-sub: after step 0 we jump to step 2, scroll to panel-2
        const nextId = isCvrNoSub.value && i === 1 ? 2 : (i + 1)
        setTimeout(() => { // wait for panel animation NLOS-2570
          const element = document.querySelector('#flow-panel-' + nextId);
          if (!element) { return }
          const bodyTop = document.body.getBoundingClientRect().top
          const elTop = element.getBoundingClientRect().top
          const offset = elTop - bodyTop
          scrollTo({ top: offset - 12 });
        }, 300);
      })
    })
    return {
      isComplete,
      FLOW_STEP_STATUS,
      panelModel,
      valueComparator,
      displayPanels,
      activePanelModel,
      activeValueComparator,
      currentStep,
      steps,
      editStep,
      nextStep,
      waitTime,
      performStepFailure,
      next,
      panels
    }
  }
}
